import React, { useState, useEffect } from 'react'
import { SelectChangeEvent } from '@mui/material'
import CountryFlag from './CountryFlag'
import { useTranslation } from 'react-i18next'
import { useCompanySpaces } from '../../queries/useCompanyQueries'
import { adjustColor } from '../../theme/ajustColor'
import { useUI } from '../../hooks/useUI'

interface CountryOption {
  id: number
  name: string
  areaCode: string
  flag: string | null
  userCreated: string
  userModified: string
}

interface Props {
  value: number | null
  options: { content: CountryOption[] } | null
  disabled: boolean
  error?: string
  selectedCountry: CountryOption | null
  setSelectedCountry: (country: CountryOption | null) => void
  label?: string
}

const CountrySelector: React.FC<Props> = ({
  value,
  options,
  error,
  selectedCountry,
  setSelectedCountry,
  disabled,
  label
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()
  const [touched, setTouched] = useState(false)

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  useEffect(() => {
    if (value && options?.content) {
      const country = options.content.find((option) => option.id === value)
      setSelectedCountry(country || null)
    }
  }, [value, options])

  const handleChange = (countryId: number) => {
    setTouched(true)
    const country = options?.content.find((option) => option.id === countryId)
    setSelectedCountry(country || null)
    setIsOpen(false)
  }

  const hasError = !!error && touched

  return (
    <div className="relative w-full">
      {label && (
        <label style={{ color: colors?.text }} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full px-3 py-2 border rounded-md cursor-pointer select-none
          ${hasError ? 'border-red-500' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
        `}
        style={{
          backgroundColor: colors?.background,
          borderColor: colors.dark,
          color: colors?.text
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedCountry ? (
              <>
                <CountryFlag areaCode={selectedCountry.areaCode} size={20} />
                <span>{`${selectedCountry.name} (${selectedCountry.areaCode})`}</span>
              </>
            ) : (
              <span className="text-gray-500">{t('selectCountry')}</span>
            )}
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && options?.content && (
        <div
          className="absolute z-50 w-full mt-1 rounded-md shadow-lg"
          style={{
            backgroundColor: colors?.background,
            borderColor: darkMode ? colors?.dark : colors?.light,
            border: '1px solid'
          }}
        >
          <div className="py-1 max-h-60 overflow-auto">
            {options.content.map((option) => (
              <div
                key={option.id}
                className={`
                  px-4 py-2 cursor-pointer hover:bg-black hover:bg-opacity-5
                  ${selectedCountry?.id === option.id ? 'bg-black bg-opacity-5' : ''}
                `}
                onClick={() => handleChange(option.id)}
                style={{ color: colors?.text }}
              >
                <div className="inline-flex items-center gap-2">
                  <CountryFlag areaCode={option.areaCode} size={20} />
                  <span>{`${option.name} (${option.areaCode})`}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasError && <p className="mt-1 text-sm text-red-600">{t('requiredField')}</p>}
    </div>
  )
}

export default CountrySelector
